import React, { useEffect, useRef } from 'react';
import { RowItem } from '../recs/engine';
import { UserProfile } from '../data/types';
import { useAnalytics } from '../recs/analytics';
import { posterUrlFor, genreGradientFor } from '../data/posters';
import { initials } from '../utils/initials';
import './TitleCard.css';

interface Props {
  item: RowItem;
  viewer: UserProfile;
  onSelect: (item: RowItem) => void;
  surface: string;
  rowPosition: number;
}

export default function TitleCard({ item, viewer, onSelect, surface, rowPosition }: Props) {
  const { title, channel, progressPct, reason } = item;
  const gradient = genreGradientFor(title.genre_primary);
  const posterUrl = posterUrlFor(title.title_id, title.genre_primary);
  const isPremiumLocked = title.access_tier === 'Premium' && viewer.access_tier === 'Free';
  const showAdBadge = viewer.access_tier === 'Free' && title.is_ad_supported;
  const isLive = title.content_type === 'Live TV';
  const { logImpression, logClick, logDwell } = useAnalytics();
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverStartRef = useRef<number | null>(null);

  useEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          logImpression({ titleId: title.title_id, titleName: title.title_name, surface, rowPosition });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title.title_id]);

  const handleSelect = () => {
    logClick({ titleId: title.title_id, titleName: title.title_name, surface, rowPosition });
    onSelect(item);
  };

  const handleMouseEnter = () => {
    hoverStartRef.current = Date.now();
  };

  const handleMouseLeave = () => {
    if (hoverStartRef.current === null) return;
    const durationMs = Date.now() - hoverStartRef.current;
    hoverStartRef.current = null;
    logDwell({ titleId: title.title_id, titleName: title.title_name, durationMs });
  };

  return (
    <div
      ref={cardRef}
      className="title-card"
      title={title.title_name}
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
    >
      <div
        className="title-card__poster"
        style={
          posterUrl
            ? { backgroundImage: `url(${posterUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { backgroundImage: gradient }
        }
      >
        {!posterUrl && <span className="title-card__initials">{initials(title.title_name)}</span>}
        {posterUrl && <div className="title-card__scrim" />}

        <div className="title-card__badges">
          {isLive && <span className="badge badge--live">LIVE</span>}
          {isPremiumLocked && <span className="badge badge--premium">Premium</span>}
          {showAdBadge && !isLive && <span className="badge badge--ad">Ads</span>}
        </div>

        {channel && <span className="title-card__channel">{channel.channel_name}</span>}

        {typeof progressPct === 'number' && (
          <div className="title-card__progress">
            <div className="title-card__progress-fill" style={{ width: `${Math.min(100, progressPct)}%` }} />
          </div>
        )}
      </div>

      <div className="title-card__meta">
        <p className="title-card__name">{title.title_name}</p>
        <p className="title-card__sub">
          {title.language} · {title.genre_primary}
          {reason ? ` · ${reason}` : ''}
        </p>
      </div>
    </div>
  );
}
